import { IntervalEntryKind } from 'api/report/types';
import {
    IntervalEntryDraft,
    IntervalEntryErrors,
    IntervalEntryValidations,
} from './editing';

export function getIntervalDraftMinutes(
    draft: IntervalEntryDraft,
    errors: IntervalEntryErrors | undefined
): number | null {
    if (
        !errors ||
        errors.startDate ||
        errors.startTime ||
        errors.endDate ||
        errors.endTime ||
        errors.timeRange ||
        errors.breakMinutes ||
        !draft.startAt ||
        !draft.endAt
    ) {
        return null;
    }
    // Timestamp offsets preserve actual elapsed time across clock changes.
    return (
        (Date.parse(draft.endAt) - Date.parse(draft.startAt)) / 60_000 -
        Number(draft.breakMinutes)
    );
}

export function getIntervalDraftDistance(
    draft: IntervalEntryDraft,
    errors: IntervalEntryErrors | undefined
): number | null {
    if (!errors || errors.distanceKm || draft.distanceKm.trim() === '') {
        return null;
    }
    return Number(draft.distanceKm);
}

export interface IntervalKindSummary {
    minutes: number | null;
    count: number;
}

export interface IntervalSummary {
    byKind: Partial<Record<IntervalEntryKind, IntervalKindSummary>>;
    totalMinutes: number;
    sickMinutes: number;
    totalDistanceKm: number;
}

interface TimeRange {
    start: number;
    end: number;
}

/**
 * Count each instant once by merging overlapping ranges before measuring them.
 * Example, sorted by start: 12–15, 14–16, 14–15.
 * Keep 12–15, extend its end to 16, then ignore 14–15 because it is covered.
 * The merged range is 12–16: 4 hours. If 18–19 follows, add those 4 hours
 * before starting a new range; the result is 5 hours, excluding the gap.
 */
function getUniqueMinutes(ranges: TimeRange[]): number {
    if (ranges.length === 0) return 0;
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    let { start, end } = sorted[0];
    let milliseconds = 0;
    for (const range of sorted.slice(1)) {
        if (range.start > end) {
            // A gap means the current merged range is complete. Count its
            // duration once, then make the next range the current one.
            milliseconds += end - start;
            start = range.start;
            end = range.end;
        } else {
            // The ranges overlap or touch. Extend the current end only if
            // the next range ends later; otherwise it is already covered.
            end = Math.max(end, range.end);
        }
    }
    // No following gap closes the last merged range, so count it here.
    return (milliseconds + end - start) / 60_000;
}

function getSummaryMinutes(entries: IntervalEntryDraft[]): number {
    const ranges = entries.map(entry => ({
        start: Date.parse(entry.startAt),
        end: Date.parse(entry.endAt),
    }));
    const breakMinutes = entries.reduce(
        (total, entry) => total + Number(entry.breakMinutes),
        0
    );
    // Breaks have no timestamps. Deduct each row's break once after merging,
    // assuming no work is reported during a break, and never show negative time.
    return Math.max(0, getUniqueMinutes(ranges) - breakMinutes);
}

export function getIntervalDraftSummary(
    entries: IntervalEntryDraft[],
    entryValidation: IntervalEntryValidations
): IntervalSummary {
    const groups = new Map<
        IntervalEntryKind,
        { entries: IntervalEntryDraft[]; count: number }
    >();
    const workingEntries: IntervalEntryDraft[] = [];
    const sickEntries: IntervalEntryDraft[] = [];
    let totalDistanceKm = 0;
    for (const entry of entries) {
        const errors = entryValidation[entry.uuid]?.errors;
        const minutes = getIntervalDraftMinutes(entry, errors);
        totalDistanceKm += getIntervalDraftDistance(entry, errors) ?? 0;
        if (entry.isSick) {
            if (minutes !== null) sickEntries.push(entry);
            continue;
        }
        const group = groups.get(entry.kind) ?? { entries: [], count: 0 };
        group.count++;
        if (minutes !== null) {
            group.entries.push(entry);
            workingEntries.push(entry);
        }
        groups.set(entry.kind, group);
    }
    return {
        byKind: Object.fromEntries(
            [...groups].map(([kind, group]) => [
                kind,
                {
                    minutes:
                        group.entries.length > 0
                            ? getSummaryMinutes(group.entries)
                            : null,
                    count: group.count,
                },
            ])
        ),
        // Merge across categories too: their individual totals can overlap.
        totalMinutes: getSummaryMinutes(workingEntries),
        sickMinutes: getSummaryMinutes(sickEntries),
        totalDistanceKm,
    };
}
